import styled from "styled-components";
import { usePage, Link } from "@inertiajs/react";
import { Wifi, Users, Maximize } from "lucide-react";

import Media from "../_kit/Media";
import Card from "../_kit/Card";
import {
  Section,
  SectionHeader,
  Lede,
  Heading,
  Grid,
  Eyebrow,
  Container,
} from "../_kit/Section";
import { roomPath } from "../../../components/insights/paths";
import { useLandingLanguage } from "../../../contexts/landing-language";
import { addThousandSeparator } from "../../../util";
import {
  weight,
  type,
  space,
  motion,
  leading,
  font,
  color,
} from "../../../styles/tokens";

/* Room cards now link to /hebe/rooms/:slug.
 *
 * Those pages are server-rendered, carry their own SEO and breadcrumbs, and sit in the sitemap —
 * but nothing on the site linked to them. Every card pointed at the #booking anchor instead, so the
 * only way in was the sitemap or a direct URL. The card also never showed price_from, which the CMS
 * has carried all along.
 */

const RoomCard = styled(Card)``;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${space[3]};
  padding: ${space[5]};
`;

const Name = styled.h3`
  margin: 0;
  font-family: ${font.display};
  font-size: ${type.h3};
  font-weight: ${weight.regular};
  line-height: ${leading.snug};
  color: ${color.ink};
`;

const Desc = styled.p`
  margin: 0;
  font-size: ${type.sm};
  line-height: ${leading.relaxed};
  color: ${color.ink500};
  /* Clamped so ragged CMS copy cannot make one card in a row twice the height of its neighbours. */
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Facts = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${space[4]};
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: ${type.sm};
  color: ${color.ink700};

  li {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

const Foot = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${space[3]};
  margin-top: auto;
  padding-top: ${space[4]};
  border-top: 1px solid ${color.border};
`;

const Price = styled.p`
  margin: 0;
  font-size: ${type.sm};
  color: ${color.ink500};

  strong {
    display: block;
    font-family: ${font.display};
    font-size: ${type.h4};
    font-weight: ${weight.semibold};
    color: ${color.ink};
  }
`;

const CardLink = styled(Link)`
  font-size: ${type.sm};
  font-weight: ${weight.semibold};
  color: ${color.primaryText};
  border-bottom: 1px solid ${color.gold};
  transition: color ${motion.base};

  /* Stretched so the whole card is one hit target, while the accessible name stays on this link
   * alone rather than being read out as the entire card's text. */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
  }

  &:hover {
    color: ${color.primary};
  }
`;

const Rooms = (props) => {
  const { room_types: roomTypes, locale } = usePage().props;
  const { t } = useLandingLanguage();
  const copy = t.rooms;
  const language = locale === "id" ? "id" : "en";

  return (
    <Section id="rooms">
      <Container>
        <SectionHeader>
          <Eyebrow>{copy.subtitle}</Eyebrow>
          <Heading>{copy.title}</Heading>
          <Lede>{copy.description}</Lede>
        </SectionHeader>

        <Grid $cols={3}>
          {(roomTypes || []).map((room) => (
            <RoomCard key={room.slug || room.name} $interactive>
              <Media
                source={(room.images || [])[0] || (room.image_urls || [])[0]}
                alt={room.name}
                ratio="4 / 3"
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
              />
              <Body>
                <Name>{room.name}</Name>
                <Desc>{room.short_desc}</Desc>
                <Facts>
                  {room.number_of_rooms ? (
                    <li>
                      <Users size={16} aria-hidden="true" />
                      {room.number_of_rooms} {copy.roomsUnit}
                    </li>
                  ) : null}
                  {room.luas_m2 ? (
                    <li>
                      <Maximize size={16} aria-hidden="true" />
                      {room.luas_m2} m²
                    </li>
                  ) : null}
                  <li>
                    <Wifi size={16} aria-hidden="true" />
                    {copy.wifi}
                  </li>
                </Facts>
                <Foot>
                  {room.price_from ? (
                    <Price>
                      {copy.fromLabel}
                      <strong>
                        Rp {addThousandSeparator(String(room.price_from))}
                      </strong>
                      {copy.perNight}
                    </Price>
                  ) : (
                    <span />
                  )}
                  {room.slug ? (
                    <CardLink href={roomPath(language, room.slug)}>
                      {copy.viewRoom}
                    </CardLink>
                  ) : null}
                </Foot>
              </Body>
            </RoomCard>
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default Rooms;
